import { MutableRefObject, useCallback, useState } from 'react';
import { fetchAISolutionWithAgentEnablement } from '../../../../utility/aiOpportunityUtils';
import { getEnterpriseTechnology } from '../../../../services/commonService';
import { ENVIRONMENT_TYPE } from '../../../../config/env';
import { makeAiEnablementSyncCall, makeDataSourcesSyncCall } from '../solutionApiService';
import { mergeAgentSyncData, mergeSyncResponses } from '../solutionUtils';

interface UseSolutionFetchOptions {
  selectedSolutionId: string | number | null;
  selectedSolutionFromLibrary?: { vault?: boolean } | null;
  diagramId?: string;
  currentOrg: string;
  isIdeation: boolean;
  processType: string;
  isVersionViewMode?: boolean;
  hasFetchedEnterpriseTechRef: MutableRefObject<boolean>;
  setAiSolutionData?: (data: any) => void;
  setAgentLlmResponse: (data: any[]) => void;
  setLlmResponseData: (data: any[]) => void;
  setIsLoadingAgentLlmResponse: (value: boolean) => void;
  setLlmResponseLoading: (value: boolean) => void;
  setUpdatedSolutionData: (data: any) => void;
  setHasEnterpriseTechApplied: (value: boolean) => void;
}

interface UseSolutionFetchResult {
  queryLoading: boolean;
  queryError: any;
  fetchSolutionById: () => Promise<void>;
}

const mergeVaultFromLibraryHint = <T extends Record<string, unknown>>(
  response: T,
  vaultHint?: boolean
): T => ({
  ...response,
  vault: typeof response.vault === 'boolean' ? response.vault : (vaultHint ?? false),
});

export const useSolutionFetch = ({
  selectedSolutionId,
  selectedSolutionFromLibrary,
  diagramId,
  currentOrg,
  isIdeation,
  processType,
  isVersionViewMode,
  hasFetchedEnterpriseTechRef,
  setAiSolutionData,
  setAgentLlmResponse,
  setLlmResponseData,
  setIsLoadingAgentLlmResponse,
  setLlmResponseLoading,
  setUpdatedSolutionData,
  setHasEnterpriseTechApplied,
}: UseSolutionFetchOptions): UseSolutionFetchResult => {
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryError, setQueryError] = useState<any>(null);

  const fetchSolutionById = useCallback(async (): Promise<void> => {
    if (!selectedSolutionId) return;

    if (String(selectedSolutionId).startsWith('mock-') || isVersionViewMode) {
      setQueryLoading(false);
      return;
    }

    setQueryLoading(true);
    setQueryError(null);
    setLlmResponseData([]);
    setAgentLlmResponse([]);

    try {
      const response = await fetchAISolutionWithAgentEnablement({
        solutionId: String(selectedSolutionId),
        diagramId: diagramId || '',
        organizationId: currentOrg,
      });

      if (!response) throw new Error('Invalid response structure');

      const responseWithVault = mergeVaultFromLibraryHint(
        response as Record<string, unknown>,
        selectedSolutionFromLibrary?.vault
      );
      setAiSolutionData?.(responseWithVault);

      // For custom processes only: trigger an immediate first-time sync when
      // the solution has an `agent` key but its value is null (never synced).
      if (processType === 'custom') {
        setQueryLoading(false);

        const hasAgentKey = Object.prototype.hasOwnProperty.call(
          response as Record<string, unknown>,
          'agent'
        );
        const agentValue = hasAgentKey ? (response as any).agent : undefined;

        if (hasAgentKey && agentValue == null) {
          // Claim the sync slot so useEnterpriseTechSync does not start a
          // duplicate sync on the same solution while we are already running.
          hasFetchedEnterpriseTechRef.current = true;

          try {
            setIsLoadingAgentLlmResponse(true);
            setLlmResponseLoading(true);

            const enterpriseTechResponse = await getEnterpriseTechnology(currentOrg);
            const hasEnterpriseTech =
              !!enterpriseTechResponse?.industry_data &&
              Object.keys(enterpriseTechResponse.industry_data).length > 0;

            // Flip the gate that controls the Agents tab so the shimmer
            // renders instead of the "Enablement Not Available" placeholder.
            setHasEnterpriseTechApplied(hasEnterpriseTech);

            if (!hasEnterpriseTech) return;

            const additionalPayload = {
              llm_agent: (response as any).llm_agent,
              solution_enabler: (response as any).solution_enabler,
              diagram_id: diagramId,
              organization_id: currentOrg,
              type: ENVIRONMENT_TYPE,
            };

            const [dataSourcesResponse, enablementResponse] = await Promise.all([
              makeDataSourcesSyncCall(
                response,
                enterpriseTechResponse,
                additionalPayload,
                processType
              ),
              makeAiEnablementSyncCall(
                response,
                enterpriseTechResponse,
                additionalPayload,
                processType
              ),
            ]);

            const combined = mergeSyncResponses(dataSourcesResponse, enablementResponse);

            const agentResponseData =
              combined?.agent_classification ||
              combined?.ai_enablement?.enablement_matrix?.embedded ||
              combined?.embedded ||
              [];

            setAgentLlmResponse(agentResponseData);
            setLlmResponseData(combined?.application_in_use || combined?.ai_solution || []);

            const merged = mergeVaultFromLibraryHint(
              mergeAgentSyncData(response, combined) as Record<string, unknown>,
              selectedSolutionFromLibrary?.vault
            );
            setUpdatedSolutionData(merged);
            setAiSolutionData?.(merged);
          } finally {
            setIsLoadingAgentLlmResponse(false);
            setLlmResponseLoading(false);
          }
        }
        return;
      }
    } catch (err: unknown) {
      setQueryError(err);
      throw err;
    } finally {
      setQueryLoading(false);
    }
  }, [
    selectedSolutionId,
    selectedSolutionFromLibrary?.vault,
    diagramId,
    currentOrg,
    setAiSolutionData,
    isIdeation,
    processType,
    isVersionViewMode,
    setAgentLlmResponse,
    setIsLoadingAgentLlmResponse,
    setLlmResponseData,
    setLlmResponseLoading,
    setUpdatedSolutionData,
  ]);

  return { queryLoading, queryError, fetchSolutionById };
};
